import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/utils/shadcn";
import { Icon } from "@/shared/ui/Icon";

export type ToastType = "success" | "error" | "info" | "progress";

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  description?: string;
  keepOpen?: boolean;
  cancelable?: boolean;
  onCancel?: () => void;
  subtle?: boolean;
  duration?: number;
}

interface ToastContextType {
  showToast: (toast: Omit<Toast, "id">) => void;
  closeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timeoutRefs = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  const functionsRef = useRef<{
    showToast: (toast: Omit<Toast, "id">) => void;
    closeToast: (id: string) => void;
  } | null>(null);

  // Show toast
  const showToast = useCallback((toast: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { ...toast, id }]);

    // Only set timeout for toasts that should auto-close
    if (!toast.keepOpen) {
      const timeout = setTimeout(() => {
        // Check if toast still exists before trying to close it
        if (functionsRef.current?.closeToast) {
          functionsRef.current.closeToast(id);
        }
        timeoutRefs.current.delete(id);
      }, toast.duration ?? 4000);
      timeoutRefs.current.set(id, timeout);
    }
  }, []);

  // Close toast
  const closeToast = useCallback((id: string) => {
    // Clear the timeout if it exists
    const timeout = timeoutRefs.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutRefs.current.delete(id);
    }

    // Only update state if the toast still exists to prevent unnecessary re-renders
    setToasts((prev) => {
      const exists = prev.some((t) => t?.id === id);
      if (!exists) return prev; // No change needed
      return prev.filter((t) => t?.id !== id);
    });
  }, []);

  // Store functions in ref to avoid circular dependency
  useEffect(() => {
    functionsRef.current = { showToast, closeToast };
  }, [showToast, closeToast]);

  // Listen for show-toast events
  useEffect(() => {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent;
      const toast: Omit<Toast, "id"> = customEvent.detail;
      showToast(toast);
    };

    const closeHandler = (e: Event) => {
      const customEvent = e as CustomEvent;
      const id = customEvent.detail?.id;
      if (id) {
        closeToast(id);
      } else {
        // Close all toasts if no specific ID provided
        setToasts([]);
        // Clear all timeouts
        timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
        timeoutRefs.current.clear();
      }
    };

    window.addEventListener("show-toast", handler as EventListener);
    window.addEventListener("close-toast", closeHandler as EventListener);
    return () => {
      window.removeEventListener("show-toast", handler as EventListener);
      window.removeEventListener("close-toast", closeHandler as EventListener);
      // Clear all timeouts on cleanup
      timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
      timeoutRefs.current.clear();
    };
  }, [showToast, closeToast]);

  return (
    <ToastContext.Provider value={{ showToast, closeToast }}>
      {children}
      {/* Toast container */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 items-end">
        {toasts.map((toast) => (
          <React.Fragment key={toast.id}>
            {toast && (
              <ToastMessage
                toast={toast}
                onClose={() => closeToast(toast.id)}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    console.error("useToast must be used within a ToastProvider");
    return {
      showToast: () => {},
      closeToast: () => {},
    };
  }
  return context;
};

// ToastMessage component
const ToastMessage: React.FC<{ toast: Toast; onClose: () => void }> = ({
  toast,
  onClose,
}) => {
  const { t } = useTranslation();

  // Safety check for toast object
  if (!toast || !toast.id) {
    return null;
  }

  let icon;
  let bg;
  let text;
  let border;
  let size = "min-w-[280px] max-w-xs";

  try {
    // Subtle variant - small, muted gray design
    if (toast.subtle) {
      bg = "bg-gray-100";
      text = "text-gray-500";
      border = "border-gray-200";
      size = "min-w-0";
      icon =
        toast.type === "success" ? (
          <Icon icon="Check" size="xs" className="text-gray-400" />
        ) : null;
    } else {
      switch (toast.type) {
        case "success":
          bg = "bg-green-600 min-w-[380px]";
          text = "text-white";
          icon = (
            <Icon
              icon="Check"
              color="green"
              size="sm"
              className="rounded-full items-center justify-center bg-green-500 text-green-600"
            />
          );
          border = "border-green-600";
          break;
        case "error":
          bg = "bg-red-100";
          text = "text-red-800";
          border = "border-red-300";
          break;
        case "info":
          bg = "bg-blue-100";
          text = "text-blue-800";
          border = "border-blue-300";
          break;
        case "progress":
          icon = <Icon icon="Loading" className="animate-spin" />;
          bg = "bg-gray-900";
          text = "text-white";
          break;
        default:
          icon = null;
          bg = "bg-gray-100";
          text = "text-gray-800";
          border = "border-gray-300";
      }
    }
  } catch (error) {
    console.error("Error rendering toast:", error);
    // Fallback to default styling
    icon = null;
    bg = "bg-gray-100";
    text = "text-gray-800";
    border = "border-gray-300";
  }

  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-lg shadow-lg border z-[1000]",
        toast.subtle ? "px-3 py-2 text-sm" : "px-4 py-3 gap-3",
        size,
        bg,
        text,
        border
      )}
    >
      {icon}
      <div className="flex flex-1 gap-2 items-center">
        {toast.message && (
          <div className={toast.subtle ? "font-normal" : "font-semibold"}>
            {t(toast.message)}
          </div>
        )}
        {toast.description && (
          <div className="text-xs">{t(toast.description)}</div>
        )}
      </div>
      {!toast.keepOpen && !toast.subtle && (
        <button
          onClick={onClose}
          className="ml-2 text-lg opacity-60 hover:opacity-100"
        >
          ×
        </button>
      )}
      {toast.cancelable && toast.onCancel && (
        <button onClick={toast.onCancel} className="ml-2 text-xs underline">
          {t("common.cancel")}
        </button>
      )}
    </div>
  );
};
